/**
 * Stock Your Kitchen Page (Pantry)
 * 
 * Card-based pantry inventory management with receipt tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/authContext';
import { 
    getKitchenStock, 
    addStockItem, 
    updateStockItem, 
    deleteStockItem,
    getReceipts,
    deleteReceipt
} from '../services/api';
import ReceiptUpload from '../components/pantry/ReceiptUpload';
import '../styles/modal.css';

interface StockItem {
    id: number;
    ingredient_name: string;
    quantity: number;
    unit: string;
    expiration_date: string | null;
    low_stock_threshold: number | null;
    image_url: string | null;
    display_order: number | null;
    last_updated: string;
}

interface Receipt {
    id: number;
    image_url: string;
    store_name: string | null;
    extracted_items: Array<{
        name: string;
        quantity: number;
        unit: string;
        price?: number;
        confidence?: string;
    }>;
    uploaded_at: string;
    processed_at: string | null;
}

export default function StockYourKitchenPage() {
    const { user } = useAuth();
    const [stock, setStock] = useState<StockItem[]>([]);
    const [receipts, setReceipts] = useState<Receipt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddItem, setShowAddItem] = useState(false);
    const [showReceiptUpload, setShowReceiptUpload] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
    const [newItem, setNewItem] = useState({
        ingredient_name: '',
        quantity: 0,
        unit: 'count',
        expiration_date: '',
        low_stock_threshold: 0
    });

    const loadStock = useCallback(async () => {
        if (!user) return;
        
        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            const response: any = await getKitchenStock(token);
            setStock(response.stock || []);
        } catch (err: any) {
            console.error('Error loading stock:', err);
            setError(err.message || 'Failed to load kitchen stock');
        } finally {
            setLoading(false);
        }
    }, [user]);

    const loadReceipts = useCallback(async () => {
        if (!user) return;

        try {
            const token = await user.getIdToken();
            const response: any = await getReceipts(token);
            setReceipts(response.receipts || []);
        } catch (err: any) {
            console.error('Error loading receipts:', err);
            setError(err.message || 'Failed to load receipts');
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            loadStock();
            loadReceipts();
        }
    }, [user, loadStock, loadReceipts]);

    const handleReceiptProcessed = useCallback(() => {
        loadReceipts();
        loadStock();
    }, [loadReceipts, loadStock]);

    const handleAddItem = async () => {
        if (!user || !newItem.ingredient_name) return;
        
        try {
            setError(null);
            const token = await user.getIdToken();
            const itemData: any = {
                ingredient_name: newItem.ingredient_name,
                quantity: newItem.quantity,
                unit: newItem.unit
            };
            
            // Only include optional fields if they have values
            if (newItem.expiration_date) {
                itemData.expiration_date = newItem.expiration_date;
            }
            
            if (newItem.low_stock_threshold > 0) {
                itemData.low_stock_threshold = newItem.low_stock_threshold;
            }
            
            await addStockItem(itemData, token);
            
            setNewItem({
                ingredient_name: '',
                quantity: 0,
                unit: 'count',
                expiration_date: '',
                low_stock_threshold: 0
            });
            setShowAddItem(false);
            loadStock();
        } catch (err: any) {
            console.error('Error adding item:', err);
            setError(err.message || 'Failed to add item');
        }
    };

    const handleDeleteItem = async (itemId: number) => {
        if (!user || !window.confirm('Delete this item from your pantry?')) return;
        
        try {
            setError(null);
            const token = await user.getIdToken();
            await deleteStockItem(itemId, token);
            loadStock();
        } catch (err: any) {
            console.error('Error deleting item:', err);
            setError(err.message || 'Failed to delete item');
        }
    };

    const handleDeleteReceipt = async (receiptId: number) => {
        if (!user || !window.confirm('Delete this receipt? This will also remove associated items from your pantry.')) return;
        
        try {
            setError(null);
            const token = await user.getIdToken();
            await deleteReceipt(receiptId, token);
            setSelectedReceipt(null);
            loadReceipts();
            loadStock();
        } catch (err: any) {
            console.error('Error deleting receipt:', err);
            setError(err.message || 'Failed to delete receipt');
        }
    };

    const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            const item = stock.find(i => i.id === itemId);
            if (!item) return;
            
            await updateStockItem(itemId, {
                quantity: newQuantity,
                unit: item.unit,
                expiration_date: item.expiration_date,
                low_stock_threshold: item.low_stock_threshold
            }, token);
            loadStock();
        } catch (err: any) {
            console.error('Error updating quantity:', err);
            setError(err.message || 'Failed to update quantity');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0 py-8">
                <div className="bg-neutral-0 p-6 hairline">
                    <p className="font-sans text-base text-neutral-900 opacity-60">Loading pantry...</p>
                </div>
            </div>
        );
    }

    // Filter items with quantity > 0
    const activeItems = stock.filter(item => item.quantity > 0);

    return (
        <div className="min-h-screen bg-neutral-50 px-0 py-8">
            {/* Header */}
            <div className="bg-neutral-0 p-6 hairline mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <h1 className="font-sans text-5xl font-medium text-[#1b263b]">
                            Pantry
                        </h1>
                        <span className="px-2 py-1 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider">BETA</span>
                    </div>
                    <button
                        onClick={() => setShowAddItem(true)}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                        style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                    >
                        Add Item
                    </button>
                </div>
                <p className="font-sans text-base text-neutral-900 opacity-60">
                    Manage your pantry inventory. Items are automatically tracked from uploaded receipts.
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-600 text-red-900 p-4 mb-6 hairline">
                    <p className="font-sans text-sm">{error}</p>
                </div>
            )}

            {/* Pantry Items Grid */}
            {activeItems.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2 mb-6">
                    {activeItems.map((item) => (
                        <div
                            key={item.id}
                            className="bg-neutral-0 hairline p-2 flex flex-col aspect-square"
                        >
                            <div className="flex items-start justify-between mb-1">
                                <h3 className="font-sans text-xs font-medium text-neutral-900 flex-1 line-clamp-2 leading-tight">
                                    {item.ingredient_name}
                                </h3>
                                <button
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="text-red-600 hover:opacity-60 transition-opacity font-mono text-xs ml-1 flex-shrink-0"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            <div className="mb-1 flex-1">
                                <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 leading-tight">
                                    {item.quantity} {item.unit}
                                </p>
                                {item.expiration_date && (
                                    <p className="font-sans text-xs text-neutral-900 opacity-60 mt-0.5 leading-tight">
                                        {new Date(item.expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </p>
                                )}
                            </div>
                            
                            <div className="flex gap-1 mt-auto">
                                <button
                                    onClick={() => handleUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                                    className="flex-1 px-1 py-0.5 font-mono text-xs uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                                >
                                    −
                                </button>
                                <button
                                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                    className="flex-1 px-1 py-0.5 font-mono text-xs uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-neutral-0 p-6 hairline mb-6">
                    <p className="font-sans text-base text-neutral-900 opacity-60">
                        No items in your pantry yet. Add items manually or upload receipts to get started.
                    </p>
                </div>
            )}

            {/* Receipts Section */}
            <div className="bg-neutral-0 p-6 hairline mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-sans text-3xl font-medium text-[#1b263b]">
                        Receipts
                    </h2>
                    <button
                        onClick={() => setShowReceiptUpload(true)}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                    >
                        Upload Receipt
                    </button>
                </div>

                {receipts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {receipts.map((receipt) => (
                            <div
                                key={receipt.id}
                                className="bg-neutral-50 hairline p-4 cursor-pointer hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                                onClick={() => setSelectedReceipt(receipt)}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <h3 className="font-sans text-base font-medium mb-1">
                                            {receipt.store_name || 'Receipt'}
                                        </h3>
                                        <p className="font-mono text-xs uppercase tracking-wider opacity-60">
                                            {new Date(receipt.uploaded_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteReceipt(receipt.id);
                                        }}
                                        className="text-red-600 hover:opacity-60 transition-opacity font-mono text-xs ml-2"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <p className="font-sans text-xs opacity-60 mt-2">
                                    {receipt.extracted_items?.length || 0} items
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-neutral-50 p-6 hairline text-center">
                        <p className="font-sans text-base text-neutral-900 opacity-60 mb-4">
                            No receipts uploaded yet. Upload your first receipt to automatically track pantry items.
                        </p>
                        <button
                            onClick={() => setShowReceiptUpload(true)}
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                            style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                        >
                            Upload Receipt
                        </button>
                    </div>
                )}
            </div>

            {/* Add Item Modal */}
            {showAddItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-neutral-0 p-6 hairline max-w-md w-full mx-4">
                        <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-4">Add Item to Pantry</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-wider text-neutral-900 block mb-1">
                                    Ingredient Name
                                </label>
                                <input
                                    type="text"
                                    value={newItem.ingredient_name}
                                    onChange={(e) => setNewItem({ ...newItem, ingredient_name: e.target.value })}
                                    className="w-full p-2 hairline font-sans text-sm text-neutral-900"
                                    placeholder="e.g., Milk"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-wider text-neutral-900 block mb-1">
                                        Quantity
                                    </label>
                                    <input
                                        type="number"
                                        value={newItem.quantity}
                                        onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
                                        className="w-full p-2 hairline font-sans text-sm text-neutral-900"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-wider text-neutral-900 block mb-1">
                                        Unit
                                    </label>
                                    <select
                                        value={newItem.unit}
                                        onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                                        className="w-full p-2 hairline font-sans text-sm text-neutral-900"
                                    >
                                        <option value="count">count</option>
                                        <option value="cups">cups</option>
                                        <option value="oz">oz</option>
                                        <option value="lbs">lbs</option>
                                        <option value="tbsp">tbsp</option>
                                        <option value="tsp">tsp</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => {
                                        setShowAddItem(false);
                                        setNewItem({
                                            ingredient_name: '',
                                            quantity: 0,
                                            unit: 'count',
                                            expiration_date: '',
                                            low_stock_threshold: 0
                                        });
                                    }}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddItem}
                                    disabled={!newItem.ingredient_name}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                                    style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Receipt Upload Modal */}
            {showReceiptUpload && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-neutral-0 p-6 hairline max-w-2xl w-full mx-4 my-8">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-sans text-xl font-medium text-[#1b263b]">Upload Receipt</h3>
                            <button
                                onClick={() => setShowReceiptUpload(false)}
                                className="text-neutral-900 hover:opacity-60 transition-opacity font-mono text-lg border-none bg-transparent p-0 m-0"
                            >
                                ✕
                            </button>
                        </div>
                        <ReceiptUpload onReceiptProcessed={() => {
                            handleReceiptProcessed();
                            setShowReceiptUpload(false);
                        }} />
                    </div>
                </div>
            )}

            {/* Receipt Detail Modal */}
            {selectedReceipt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-neutral-0 p-6 hairline max-w-2xl w-full mx-4 my-8">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-sans text-xl font-medium text-[#1b263b]">
                                    {selectedReceipt.store_name || 'Receipt'}
                                </h3>
                                <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mt-1">
                                    {new Date(selectedReceipt.uploaded_at).toLocaleDateString()}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleDeleteReceipt(selectedReceipt.id)}
                                    className="px-3 py-1 font-mono text-xs uppercase tracking-wider bg-red-600 text-white border-none hover:opacity-60"
                                >
                                    Delete
                                </button>
                                <button
                                    onClick={() => setSelectedReceipt(null)}
                                    className="text-neutral-900 hover:opacity-60 transition-opacity font-mono text-lg border-none bg-transparent p-0 m-0"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        
                        {selectedReceipt.image_url && (
                            <div className="mb-4">
                                <img
                                    src={selectedReceipt.image_url}
                                    alt="Receipt"
                                    className="max-w-full h-auto hairline"
                                />
                            </div>
                        )}
                        
                        <div>
                            <h4 className="font-sans text-lg font-medium text-[#1b263b] mb-3">
                                Extracted Items ({selectedReceipt.extracted_items?.length || 0})
                            </h4>
                            <div className="space-y-2">
                                {selectedReceipt.extracted_items?.map((item, index) => (
                                    <div
                                        key={index}
                                        className="p-3 bg-neutral-50 hairline flex items-center justify-between"
                                    >
                                        <div>
                                            <p className="font-sans text-sm font-medium text-neutral-900">
                                                {item.name}
                                            </p>
                                            <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                                {item.quantity} {item.unit}
                                            </p>
                                        </div>
                                        {item.price && (
                                            <p className="font-mono text-xs uppercase tracking-wider text-neutral-900">
                                                ${item.price.toFixed(2)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
